import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName } from "@/components/ui/baseUi/sonner"
import { useBoxOffers } from "@/modules/boxOffer/hooks/useBoxOffer"
import { BoxProgramDefaultSettingsFormView } from "@/modules/boxProgrammation/components/boxProgramDefaultSettingsFormView"
import { BoxProgrammationForm } from "@/modules/boxProgrammation/components/boxProgramForm"
import { BoxProgramSlotDefaultSettingsEntity } from "@/modules/boxProgrammation/entities/boxProgramSlotDefaultSettingsEntity"
import {
  getBoxProgramDraftValues,
  getBoxProgramSchema,
  getDefaultSettingsDraftValues,
  getDefaultSettingsSchema,
} from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import {
  useCreateBoxProgram,
  useFetchBoxProgram,
  useUpdateBoxProgram,
} from "@/modules/boxProgrammation/hooks/useBoxProgram"
import {
  useCreateBoxProgramSlotDefaultSettings,
  useFetchBoxProgramSlotDefaultSettings,
  useUpdateBoxProgramSlotDefaultSettings,
} from "@/modules/boxProgrammation/hooks/useBoxProgramSlotDefaultSettings"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { Path } from "@/modules/navigation/routes"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useImperativeHandle, useRef, useState } from "react"
import { toast } from "sonner"

const FormStep = {
  INFORMATION: "Information",
  DEFAULT_SETTINGS: "DefaultSettings",
} as const
type FormStep = (typeof FormStep)[keyof typeof FormStep]

export type BoxProgramFormViewRef = {
  onClose: () => Promise<boolean>
}

export function BoxProgramFormView({
  boxProgramId,
  ref,
  className,
}: {
  boxProgramId?: string
  ref: React.Ref<BoxProgramFormViewRef>
  className?: string
}) {
  const [formStep, setFormStep] = useState<FormStep>(FormStep.INFORMATION)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const confirmPopup = useConfirmPopup()
  const { hideSidebar } = useSidebar()
  const { data: boxProgram, isLoading: isLoadingBoxProgram } = useFetchBoxProgram(boxProgramId)
  const { data: defaultSettings, isLoading: isLoadingDefaultSettings } =
    useFetchBoxProgramSlotDefaultSettings(boxProgramId)
  const { mutateAsync: updateBoxProgram } = useUpdateBoxProgram()
  const { mutateAsync: createBoxProgram } = useCreateBoxProgram()
  const { mutateAsync: updateBoxProgramSlotDefaultSettings } = useUpdateBoxProgramSlotDefaultSettings()
  const { mutateAsync: createBoxProgramSlotDefaultSettings } = useCreateBoxProgramSlotDefaultSettings()
  const language = useLanguage()
  const boxNavigate = useBoxNavigate()
  const { data: boxOffers } = useBoxOffers()

  const schema = getBoxProgramSchema(!!boxProgram, language)
  const boxProgramForm = useAppForm({
    defaultValues: boxProgram ? boxProgram.toFormik() : getBoxProgramDraftValues(boxOffers.map((offer) => offer.id)),
    validators: {
      onSubmit: schema,
    },
    onSubmit: () => {
      scrollToTop()
      setFormStep("DefaultSettings")
    },
  })

  const defaultSettingsSchema = getDefaultSettingsSchema(language)
  const defaultSettingsForm = useAppForm({
    defaultValues: defaultSettings ? defaultSettings.toFormik() : getDefaultSettingsDraftValues(),
    validators: {
      onSubmit: defaultSettingsSchema,
    },
    onSubmit: async () => {
      try {
        if (boxProgram) {
          await updateBoxProgram({
            boxProgramId: boxProgram.id,
            boxProgram: boxProgramForm.state.values,
          })

          await onSubmitDefaultSettings(boxProgram.id)
        } else {
          const newBoxProgram = await createBoxProgram({
            boxProgram: boxProgramForm.state.values,
          })
          await onSubmitDefaultSettings(newBoxProgram.id)
          boxNavigate(Path.Box.ProgrammationDetails(newBoxProgram.id))
        }
      } catch (e: unknown) {
        const error = e as { code?: string; message: string }
        toast.error(word("global.error.label"), {
          description: error.message,
          classNames: errorToastClassName(),
        })
      } finally {
        setIsSubmitting(false)
      }
    },
  })
  const containerRef = useRef<HTMLDivElement>(null)

  const isLoading = isLoadingBoxProgram || isLoadingDefaultSettings

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (!boxProgramForm.state.isPristine || !defaultSettingsForm.state.isPristine) {
        const confirm = await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("box.programmation.form.closeMessage.title"),
            message: word("box.programmation.form.closeMessage.message"),
            accept: () => {
              resolve(true)
            },
            cancel: () => {
              resolve(false)
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })

        return confirm
      }
      return Promise.resolve(true)
    },
  }))

  const scrollToTop = () => {
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
  }

  const scrollToFirstError = () => {
    const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
    if (errorElements && errorElements.length > 0) {
      document
        .querySelector('[data-slot="right-sidebar-content"]')
        ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
    }
  }

  const onSubmitDefaultSettings = async (boxProgramIdToUpdate: string) => {
    try {
      const newDefaultSettings = BoxProgramSlotDefaultSettingsEntity.fromFormikToCreateOrUpdateInputDto(
        defaultSettingsForm.state.values,
      )

      if (defaultSettings) {
        await updateBoxProgramSlotDefaultSettings({
          boxProgramId: boxProgramIdToUpdate,
          defaultValues: newDefaultSettings,
        })
      } else {
        await createBoxProgramSlotDefaultSettings({
          boxProgramId: boxProgramIdToUpdate,
          defaultValues: newDefaultSettings,
        })
      }

      toast.success(
        !boxProgram ? word("programmation.form.create.success") : word("programmation.form.update.success"),
        {
          classNames: successToastClassName(),
        },
      )

      hideSidebar(false)
    } catch (e: unknown) {
      const error = e as { code?: string; message: string }
      toast.error(word("global.error.label"), {
        description: error.message,
        classNames: errorToastClassName(),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const moveToDefaultSettingsStep = async () => {
    const errors = await boxProgramForm.validateAllFields("change")
    if (!errors.some(Boolean)) {
      await boxProgramForm.handleSubmit()
    } else {
      scrollToFirstError()
    }
  }

  const onValidateDefaultSettings = async () => {
    setIsSubmitting(true)

    const errors = await defaultSettingsForm.validate("submit") // Validate the fields (with superRefine)

    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false)
      scrollToFirstError()
    } else {
      await defaultSettingsForm.handleSubmit()
    }
  }

  if (boxProgramId && isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div ref={containerRef} className={cn("flex w-full flex-col", className)}>
      {formStep === FormStep.INFORMATION ? (
        <BoxProgrammationForm
          boxProgrammation={boxProgram ?? undefined}
          form={boxProgramForm}
          onValidate={() => void moveToDefaultSettingsStep()}
        />
      ) : (
        <BoxProgramDefaultSettingsFormView
          boxProgramName={boxProgramForm.state.values.name}
          boxProgramColor={boxProgramForm.state.values.color}
          form={defaultSettingsForm}
          moveToPreviousFormStep={() => {
            setFormStep(FormStep.INFORMATION)
            scrollToTop()
          }}
          onValidate={() => void onValidateDefaultSettings()}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  )
}
